import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useEnvironmentInfo } from '../../hooks/useEnvironmentInfo';

export const ModelCompatibilityBadge: React.FC = () => {
  const { t } = useTranslation('training');
  const { data, isLoading, isError } = useEnvironmentInfo();
  const [expanded, setExpanded] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const handleCopy = useCallback(async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.pip_install);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      setCopyState('failed');
    }
  }, [data]);

  if (isLoading) return null;

  if (isError || !data) {
    return (
      <div className="text-xs text-muted-foreground italic" role="status">
        {t('modelCompatibility.loadError')}
      </div>
    );
  }

  return (
    <div
      className="model-compat-badge rounded p-2 text-xs"
      style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e293b' }}
    >
      <div className="flex items-center justify-between gap-2">
        <span>
          <span className="font-semibold">{t('modelCompatibility.title')}:</span>{' '}
          {t('modelCompatibility.trainedWith')} TF {data.tensorflow} / Keras {data.keras}
        </span>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="underline"
          style={{ color: '#0D9488', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
          aria-label={expanded ? t('modelCompatibility.collapse') : t('modelCompatibility.expand')}
        >
          {expanded ? t('modelCompatibility.collapse') : t('modelCompatibility.expand')}
        </button>
      </div>
      {expanded && (
        <div className="mt-2">
          <div className="text-xs mb-1">{t('modelCompatibility.installCommand')}</div>
          <div className="flex items-stretch gap-2">
            <code
              className="flex-1 p-1 rounded select-all"
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #cbd5e1',
                color: '#0f172a',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            >
              {data.pip_install}
            </code>
            <button
              type="button"
              onClick={handleCopy}
              className="px-2 rounded"
              style={{ backgroundColor: '#ffffff', border: '1px solid #cbd5e1', color: '#0f172a', cursor: 'pointer' }}
              aria-label={t('modelCompatibility.copyPip')}
            >
              {copyState === 'copied'
                ? t('modelCompatibility.copied')
                : copyState === 'failed'
                ? t('modelCompatibility.copyFailed')
                : t('modelCompatibility.copyPip')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
